import { CropOriginal, Devices, PlayCircleOutlined, Timelapse } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import type { GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataGridPagination } from '~/components/data-grid';
import { ByEventCharts } from '../components/ByEventCharts';
import { HorizontalBarCard } from '../components/HorizontalBarCard';
import { ReportDataGrid } from '../components/ReportDataGrid';
import { StatCard } from '../components/StatCard';
import { ZoneAndDeviceGroupDonuts } from '../components/ZoneAndDeviceGroupDonuts';
import {
  CardPaper,
  EMPTY_REPORT,
  TabRoot,
  assetChartLabel,
  avgPerEventPerDevice,
  formatMinutes,
  useColumns,
  type TopAssetRow,
} from '../lib';
import { usePlayoutReport } from './context';

export const PlayoutReportsOverview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { report } = usePlayoutReport();
  const {
    stats,
    bySponsorPlays,
    bySponsorDuration,
    byAssetPlays,
    byAssetDuration,
    byEvent,
    byZone,
    byDeviceGroup,
    topAssets,
  } = report;

  // null (not 0) when no report is loaded so the stat card and the
  // per-device averages render as "—" instead of a misleading zero.
  const uniqueDevices = report === EMPTY_REPORT ? null : stats.uniqueDevices;

  const avgPlaysPerDevice = avgPerEventPerDevice(stats.totalPlays, stats.eventCount, uniqueDevices);
  const avgDurationPerDevice = avgPerEventPerDevice(
    stats.totalDurationMs,
    stats.eventCount,
    uniqueDevices,
  );

  const baseColumns = useColumns();
  const columns = useMemo(() => {
    const anyHasSponsor = topAssets.some((r) => r.sponsor && r.sponsor.length > 0);
    return anyHasSponsor ? baseColumns : baseColumns.filter((c) => c.field !== 'sponsor');
  }, [baseColumns, topAssets]);

  return (
    <TabRoot>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PlayCircleOutlined />}
            label={t('pages.playout_reports.stats.total_plays.label')}
            value={
              avgPlaysPerDevice != null
                ? avgPlaysPerDevice.toLocaleString(undefined, { maximumFractionDigits: 1 })
                : '—'
            }
            subtitle={t('pages.playout_reports.stats.total_plays.subtitle', {
              total: stats.totalPlays.toLocaleString(),
            })}
            tooltip={t('pages.playout_reports.stats.total_plays.tooltip')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Timelapse />}
            label={t('pages.playout_reports.stats.total_airtime.label')}
            value={
              avgDurationPerDevice != null
                ? `${formatMinutes(avgDurationPerDevice).toLocaleString()} m`
                : '—'
            }
            subtitle={t('pages.playout_reports.stats.total_airtime.subtitle', {
              total: formatMinutes(stats.totalDurationMs).toLocaleString(),
              hours: Math.round(stats.totalDurationMs / 3_600_000).toLocaleString(),
            })}
            tooltip={t('pages.playout_reports.stats.total_airtime.tooltip')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CropOriginal />}
            label={t('pages.playout_reports.stats.unique_assets.label')}
            value={stats.uniqueAssets.toLocaleString()}
            tooltip={t('pages.playout_reports.stats.unique_assets.tooltip')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Devices />}
            label={t('pages.playout_reports.stats.devices_reached.label')}
            value={uniqueDevices != null ? uniqueDevices.toLocaleString() : '—'}
            subtitle={t('pages.playout_reports.stats.devices_reached.across_groups', {
              count: stats.uniqueDeviceGroups,
              groups: t('common.pluralize.device_group_plural', {
                count: stats.uniqueDeviceGroups,
              }),
            })}
            tooltip={t('pages.playout_reports.stats.devices_reached.tooltip')}
          />
        </Grid>
      </Grid>

      {bySponsorPlays.length > 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <HorizontalBarCard
              title={t('pages.playout_reports.charts.by_sponsor_plays.title')}
              labels={bySponsorPlays.map((d) => d.sponsor)}
              values={bySponsorPlays.map((d) => d.plays)}
              tooltip={t('pages.playout_reports.charts.by_sponsor_plays.tooltip')}
              onBarClick={(i) => {
                const id = bySponsorPlays[i].sponsorId;
                if (id == null) return;
                navigate({ pathname: `by-sponsor/${id}`, search });
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <HorizontalBarCard
              title={t('pages.playout_reports.charts.by_sponsor_duration.title')}
              labels={bySponsorDuration.map((d) => d.sponsor)}
              values={bySponsorDuration.map((d) => formatMinutes(d.durationMs))}
              tooltip={t('pages.playout_reports.charts.by_sponsor_duration.tooltip')}
              onBarClick={(i) => {
                const id = bySponsorDuration[i].sponsorId;
                if (id == null) return;
                navigate({ pathname: `by-sponsor/${id}`, search });
              }}
            />
          </Grid>
        </Grid>
      )}

      {byAssetPlays.length > 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <HorizontalBarCard
              title={t('pages.playout_reports.charts.by_asset_plays.title')}
              labels={byAssetPlays.map((d) => assetChartLabel(d.asset, d.sponsor))}
              values={byAssetPlays.map((d) => d.plays)}
              tooltip={t('pages.playout_reports.charts.by_asset_plays.tooltip')}
              onBarClick={(i) => {
                const row = byAssetPlays[i];
                if (row.assetId == null) return;
                navigate({ pathname: `by-asset/${row.kind}/${row.assetId}`, search });
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <HorizontalBarCard
              title={t('pages.playout_reports.charts.by_asset_duration.title')}
              labels={byAssetDuration.map((d) => assetChartLabel(d.asset, d.sponsor))}
              values={byAssetDuration.map((d) => formatMinutes(d.durationMs))}
              tooltip={t('pages.playout_reports.charts.by_asset_duration.tooltip')}
              onBarClick={(i) => {
                const row = byAssetDuration[i];
                if (row.assetId == null) return;
                navigate({ pathname: `by-asset/${row.kind}/${row.assetId}`, search });
              }}
            />
          </Grid>
        </Grid>
      )}

      <ByEventCharts events={byEvent} />

      <ZoneAndDeviceGroupDonuts
        byZone={byZone}
        byDeviceGroup={byDeviceGroup}
        totalDurationMs={stats.totalDurationMs}
      />

      <CardPaper sx={{ pb: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography component="h2" variant="subtitle1" fontWeight={600}>
            {t('pages.playout_reports.top_assets.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('pages.playout_reports.top_assets.count', { count: topAssets.length })}
          </Typography>
        </Box>
        <ReportDataGrid
          columnStateKey="overviewTopAssets"
          columns={columns}
          rows={topAssets}
          disableRowSelectionOnClick
          // Row key mirrors the service's GROUP BY (kind, sponsor_id, asset_id)
          // so rows it intentionally keeps separate — e.g. distinct apps
          // wrapping the same media — never collide.
          getRowId={(row: TopAssetRow) =>
            `${row.kind}::${row.sponsorId ?? ''}::${row.assetId ?? ''}`
          }
          onRowClick={({ row }: GridRowParams<TopAssetRow>) => {
            if (row.assetId == null) return;
            navigate({ pathname: `by-asset/${row.kind}/${row.assetId}`, search });
          }}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
            sorting: { sortModel: [{ field: 'durationMs', sort: 'desc' }] },
          }}
          slots={{ pagination: DataGridPagination }}
        />
      </CardPaper>
    </TabRoot>
  );
};
